// ─────────────────────────────────────────────────────────────
// menuConfig.js — Sidebar 메뉴 single source of truth
// ─────────────────────────────────────────────────────────────
// hiddenByDefault: 코드 default (관리자가 visibility 미설정 시 사용)
//   2026-05-25: 일반 사용자 default 를 "딥러닝(Donut 학습데이타) + HS 분류" 2개만
//   노출로 축소. 나머지 메뉴는 관리자가 명시적으로 켜야 보임.
//   2026-06-15: 오픈 기본 노출 = 딥러닝(사이드바 하드코딩) + AI HS분류 + 속견표 +
//   HS비교 + 문의·건의 5개. 결정트리검토·관세청품목분류사례·FedEx검증 등은 기본 숨김
//   → 관리자가 업체/개인별로 부여.
// category: 우측 메뉴 panel 의 그룹 라벨 (관리자 UI 정리용)
// 관리자가 D_DOCUAI_MENU_VISIBILITY / D_DOCUAI_MENU_OVERRIDE 에 row 를
// 만들면 그 값이 우선. id 는 Sidebar 의 activeTab + 백엔드 MENU_KEY 와 1:1.
// ─────────────────────────────────────────────────────────────
import {
  LayoutTemplate,
  Car,
  Users,
  CheckSquare,
  Building2,
  Search,
  GitBranch,
  MessageSquare,
  Calculator,
  Banknote,
  Bell,
  BookOpen,
  FileText,
  ListChecks,
  Database,
  BarChart3,
} from "lucide-react";

export const MENU_ITEMS = [
  // ── 🤖 학습 / 검증 ──
  {
    id: "donut-learning-data",
    label: "딥러닝 학습 데이터",
    icon: CheckSquare,
    category: "deep-learning",
    hiddenByDefault: true, // "딥러닝"(Sidebar 하드코딩 버튼)과 별개 라벨링 툴 — 기본 숨김
  },
  {
    id: "grid",
    label: "일괄 검증",
    icon: LayoutTemplate,
    category: "deep-learning",
    hiddenByDefault: true,
  },
  {
    id: "training-verify",
    label: "학습 데이터 검증",
    icon: CheckSquare,
    category: "deep-learning",
    hiddenByDefault: true,
  },
  {
    id: "training-verify-export",
    label: "학습 데이터 검증 (수출)",
    icon: CheckSquare,
    category: "deep-learning",
    hiddenByDefault: true,
  },
  {
    id: "gt-verify",
    label: "정답 검증 (AI 비교)",
    icon: CheckSquare,
    category: "deep-learning",
    hiddenByDefault: true,
  },
  {
    id: "fedex-verify",
    label: "FedEx 추출 검증",
    icon: CheckSquare,
    category: "deep-learning",
    hiddenByDefault: true, // 검수 도구 — 기본 숨김(관리자 부여). 오픈 기본=딥러닝(메인)만
  },

  // ── 🔍 HS 분류 ──
  {
    id: "hs-hub",
    label: "HS 분류",
    icon: Search,
    href: "/?audit=hs-hub",
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-ai-classify",
    label: "관세율표",
    shortLabel: "관세율표", // 모바일 하단 탭바용 (구 "AI HS 분류" — 사용자 요청 관세율표로 변경)
    icon: GitBranch,
    // 관세율표 = 속견표(quickref) 그리드 + 좌측 아코디언, noextra=1 로 전용 3버튼(전체·내품목·변경내역) 숨김.
    //   (구 onto-detail 부/류 리스트 → 속견표 그리드로 대체, 사용자 요청 2026-07-13)
    href: "/?audit=quickref&noextra=1",
    spa: true, // 리로드 없이 pushState — 메인 Sidebar 고정 + 오른쪽 embedded 전환
    category: "hs",
    hiddenByDefault: false, // ⭐ 기본 노출
    // 아코디언 하위: HS 비교·품목분류사례를 관세율표 아래에 그룹핑(통관정보와 동일 방식).
    children: [
      { label: "HS 비교", href: "/?audit=hs-compare" },
      { label: "품목분류사례", href: "/?audit=precedents" },
    ],
  },
  {
    id: "hs-ontology-review",
    label: "PROVE 결정트리 검토",
    shortLabel: "PROVE 트리",
    icon: GitBranch,
    href: "/?audit=onto-review",
    spa: true,
    category: "hs",
    hiddenByDefault: true, // 기본 숨김 — 관리자가 업체/개인별 부여(오픈 기본 5개 외)
  },
  {
    id: "hs-classifier",
    label: "HS Code 분류",
    icon: Search,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-unified-search",
    label: "속견표",
    shortLabel: "속견표",
    icon: Search,
    href: "/?audit=quickref",
    spa: true, // 앱 셸 embedded (AI HS분류와 동일 패턴) — 구 ?audit=main(독립 MainPage)은 빈화면
    category: "hs",
    // 관세율표 자체가 속견표 그리드라 메뉴 중복 → 기본 숨김(2026-07-14). 라우트는 유지
    //   (내품목·변경내역은 관세율표 상단 툴바로 이동). 관리자 menuVisibility 로 재노출 가능.
    hiddenByDefault: true,
  },
  {
    id: "hs-req-guide",
    label: "통관정보",
    shortLabel: "통관정보",
    icon: Search, // 기존 import된 lucide 아이콘 재사용
    href: "/?audit=req-guide",
    spa: true,
    category: "hs",
    hiddenByDefault: false,
    // 관세청식 아코디언: 사이드바에서 펼치면 하위 카테고리 노출(우측 서브패널 대체).
    //   count = /api/onto/req-guide/counts[countKey]. 클릭 → ?audit=req-guide&cat=<key>.
    children: [
      {
        label: "세관장확인",
        href: "/?audit=req-guide&cat=customs_check",
        countKey: "customs_check",
      },
      {
        label: "통합공고",
        href: "/?audit=req-guide&cat=integrated_notice",
        countKey: "integrated_notice",
      },
      {
        label: "수출입공고",
        href: "/?audit=req-guide&cat=customs_notice",
        countKey: "customs_notice",
      },
      {
        label: "원산지",
        href: "/?audit=req-guide&cat=origin",
        countKey: "origin",
      },
      {
        label: "전략물자",
        href: "/?audit=req-guide&cat=strategic",
        countKey: "strategic",
      },
      {
        label: "HS별 신고가이드",
        href: "/?audit=req-guide&cat=by_hsk",
        countKey: "by_hsk",
      },
      {
        label: "법령별 통관정보",
        href: "/?audit=req-guide&cat=req_by_law",
        countKey: "req_by_law",
      },
      {
        label: "수출입승인의 면제",
        href: "/?audit=req-guide&cat=approval_exempt",
        countKey: "approval_exempt",
      },
    ],
  },
  {
    id: "hs-customs-guide",
    label: "관세제도",
    shortLabel: "관세제도",
    icon: BookOpen,
    href: "/?audit=customs-guide",
    spa: true,
    category: "hs",
    hiddenByDefault: false, // 감면·간이세율·분할납부·용도세율 허브(단일 진입)
    // 아코디언 하위: 각 제도 상세 페이지(허브 카드와 동일 대상). 클릭 → 해당 ?audit= 라우트.
    children: [
      { label: "관세감면", href: "/?audit=exemption" },
      { label: "간이세율", href: "/?audit=simplified-rate" },
      { label: "분할납부", href: "/?audit=installment" },
      { label: "용도세율", href: "/?audit=enduse-rate" },
    ],
  },
  {
    id: "hs-news",
    label: "새소식",
    shortLabel: "새소식",
    icon: Bell,
    href: "/?audit=news",
    spa: true,
    category: "hs",
    hiddenByDefault: false,
  },
  {
    id: "hs-tools",
    label: "도구",
    shortLabel: "도구",
    icon: Calculator,
    category: "hs",
    hiddenByDefault: false,
    // 공개 콘텐츠를 로그인 후에는 업무 셸 안에서 연다.
    // href 없는 그룹이므로 부모 클릭은 화면을 바꾸지 않고 펼침/접힘만 수행한다.
    children: [
      { label: "관세환율", href: "/?audit=tools&tool=exchange" },
      { label: "특송운임", href: "/?audit=tools&tool=freight" },
      { label: "세금 계산기", href: "/?audit=tools&tool=taxcalc" },
      { label: "서식자료실", href: "/?audit=tools&tool=forms" },
      { label: "무역통계부호", href: "/?audit=tools&tool=trade-codes" },
      { label: "요건확인기관 연락처", href: "/?audit=tools&tool=req-agencies" },
    ],
  },
  {
    id: "hs-usage",
    label: "사용량",
    shortLabel: "사용량",
    icon: BarChart3,
    href: "/?audit=usage",
    spa: true,
    category: "hs",
    hiddenByDefault: false,
  },
  // 63차: 회사 계정 · 신고분포 정보공개 동의(사업자번호 단위 동의·회사 계정 확인 요청)
  {
    id: "hs-company",
    label: "회사·정보공개",
    shortLabel: "회사",
    icon: Building2,
    href: "/?audit=company",
    spa: true,
    category: "hs",
    hiddenByDefault: false,
  },
  // ── 관세 제도 개별 페이지: 허브(hs-customs-guide) 카드로 진입 → 사이드바 기본 숨김.
  //    라우트는 유지(허브 카드·세번상세 딥링크 정상). 관리자가 개별 재노출 가능.
  {
    id: "hs-exemption",
    label: "관세감면",
    shortLabel: "관세감면",
    icon: Search,
    href: "/?audit=exemption",
    spa: true,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-simplified-rate",
    label: "간이세율",
    shortLabel: "간이세율",
    icon: Search,
    href: "/?audit=simplified-rate",
    spa: true,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-installment",
    label: "분할납부",
    shortLabel: "분할납부",
    icon: Search,
    href: "/?audit=installment",
    spa: true,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-enduse-rate",
    label: "용도세율",
    shortLabel: "용도세율",
    icon: Search,
    href: "/?audit=enduse-rate",
    spa: true,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-compare",
    label: "HS 비교",
    shortLabel: "HS비교",
    icon: Search,
    href: "/?audit=hs-compare",
    spa: true, // 앱 셸 embedded — 나라별 세번 비교 (UNIPASS 세계HS 실시간 프록시)
    category: "hs",
    hiddenByDefault: true, // 관세율표 아코디언 하위로 이동(사이드바 최상위 노출 안 함)
  },
  {
    id: "hs-precedents-explorer",
    label: "품목분류사례",
    shortLabel: "분류사례", // 모바일 하단 탭바용
    icon: Search,
    href: "/?audit=precedents",
    spa: true, // 리로드 없이 pushState — 메인 Sidebar 고정 + 오른쪽 embedded 전환
    category: "hs",
    // 2026-07-08 CL식 마스터-디테일(참조번호·기관·결정사유 전문) 개편과 함께 기본 노출로
    // 승격(사용자 결정). 관리자가 명시적으로 끈 계정(D_DOCUAI_MENU_VISIBILITY)은 그 값 우선.
    hiddenByDefault: true, // 관세율표 아코디언 하위로 이동(사이드바 최상위 노출 안 함)
  },
  {
    id: "hs-my-reports",
    label: "품목 관리",
    shortLabel: "품목관리",
    icon: FileText,
    href: "/?audit=my-reports",
    spa: true, // 앱 셸 embedded — 품목별 결론·메모·근거·첨부파일·발급보고서 통합 관리
    category: "hs",
    hiddenByDefault: false, // 검토보고서 발급 기능과 짝 — loginHS 사용자 기본 노출
  },
  {
    id: "hs-synonyms-admin",
    label: "동의어 사전",
    icon: Search,
    href: "/?audit=synonyms",
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "hs-bulk-classify",
    label: "대량 세번분류",
    icon: ListChecks,
    href: "/?audit=bulk-classify",
    category: "hs",
    hiddenByDefault: false, // 일반 노출(관리자먼저는 나중으로, 2026-07-16) — 잡은 userId/groupId 소유자 격리
    beta: true, // 정식 오픈 전 기능보완 중 — 사이드바·화면 타이틀에 beta 배지
  },
  {
    id: "hs-master",
    label: "마스터 세번관리",
    icon: Database,
    href: "/?audit=master-hs",
    category: "hs",
    hiddenByDefault: false,
    beta: true,
  },
  {
    id: "hs-classifier-audit",
    label: "HS 분류 이력",
    icon: CheckSquare,
    category: "hs",
    hiddenByDefault: true,
  },
  {
    id: "inquiry-board",
    label: "문의·건의",
    shortLabel: "문의", // 모바일 탭바
    icon: MessageSquare,
    href: "/?audit=inquiry",
    spa: true,
    category: "hs",
    hiddenByDefault: false, // ⭐ 기본 노출 — 누구나 문의 가능
  },

  // ── 🧰 도구 (포털 페이지 미러 — 업무 중 바로 접근, 구현은 ?page= 라우트 재사용) ──
  //   clhs 사용자 습관("한 화면에서 다 찾기") 대응: 포털 전용이던 도구를 앱에서도 노출.
  {
    id: "tool-trade-codes",
    label: "무역통계부호",
    icon: BookOpen,
    href: "/?page=trade-codes",
    category: "tools",
    hiddenByDefault: false, // 문의(김은정) 대응 — 기본 노출
  },
  {
    id: "tool-freight",
    label: "운임 계산기",
    icon: Calculator,
    href: "/?page=freight",
    category: "tools",
    hiddenByDefault: false,
  },
  {
    id: "tool-taxcalc",
    label: "세금 계산기",
    icon: Calculator,
    href: "/?page=taxcalc",
    category: "tools",
    hiddenByDefault: false,
  },
  {
    id: "tool-exchange",
    label: "관세환율",
    icon: Banknote,
    href: "/?page=exchange",
    category: "tools",
    hiddenByDefault: false,
  },
  {
    id: "tool-req-agencies",
    label: "요건확인기관 연락처",
    icon: BookOpen,
    href: "/?page=req-agencies",
    category: "tools",
    hiddenByDefault: false, // 요건 법령별 소관부처·직통전화 (문의 #25 후속)
  },

  // ── 🚗 기타 ──
  {
    id: "taxpayer-matching",
    label: "납세자 매칭",
    icon: Building2,
    category: "etc",
    hiddenByDefault: true,
  },
  {
    id: "mapping",
    label: "차량 등록",
    icon: Car,
    category: "etc",
    hiddenByDefault: true,
  },
  {
    id: "legacy-registration",
    label: "거래처 등록",
    icon: Users,
    category: "etc",
    hiddenByDefault: true,
  },
];

export const MENU_CATEGORIES = [
  { id: "deep-learning", label: "학습 / 검증" },
  { id: "hs", label: "HS 분류" },
  { id: "tools", label: "도구" },
  { id: "etc", label: "기타" },
];

/**
 * resolveVisibleMenus —
 * @param {Array} menuItems — MENU_ITEMS
 * @param {object} visibilityMap — { [menuKey]: boolean }  (백엔드에서 받은 최종 merge 결과)
 * @param {boolean} isManager — true 면 visibility 무시하고 전체 노출
 * @returns 가시 메뉴 list (Sidebar 가 그대로 render)
 */
export function resolveVisibleMenus(menuItems, visibilityMap, isManager) {
  if (isManager) return menuItems;
  return menuItems.filter((m) => {
    if (
      visibilityMap &&
      Object.prototype.hasOwnProperty.call(visibilityMap, m.id)
    ) {
      return visibilityMap[m.id] === true;
    }
    return !m.hiddenByDefault;
  });
}
